import { useCallback, useEffect, useMemo, useState } from "react"
import { flushSync } from "react-dom"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Dayjs } from "utils/dayjs"
import PQueue from "p-queue"
import { uniq } from "lodash"

import useShippingOrderCart from "hooks/useShippingOrderCart"
import { Guid, IValidationRule } from "@/types"
import ValidationService from "services/ValidationService"
import CustomError from "models/CustomError"
import ShippingOrder, { ShippingOrderStatus, ShippingOrderType } from "models/ShippingOrder"
import toastMessages from "stores/toastMessages"
import { toDateTimeWithoutTimezone } from "utils/utils"
import Reservation from "models/Reservation"
import confirmModals from "stores/confirmModals"
import useDepartureArrivalDateRules from "hooks/useDepartureArrivalDateRules"
import Routes from "@/routes"
import { ICreateRequestLocation, RequestLocationType } from "models/RequestLocation"
import { IUpdateShippingOrderCartAssetDto } from "models/ShippingOrderCartAsset"
import { ErrorCode, UserPermission } from "config/constants"
import ShippingOrderCart, { IUpdateShippingOrderCartDto } from "models/ShippingOrderCart"
import useShippingOrderAssetsExtended from "./useShippingOrderAssetsExtended"
import useRequestLocations from "hooks/useRequestLocations"
import ShippingOrderAssetExtended from "models/ShippingOrderAssetExtended"
import AssetService from "services/AssetService"
import { IShippingOrderAssetsSort } from "../config"
import Asset from "models/Asset"
import { ISelectedFreightUnit } from "../SelectFreightUnitsModal"
import { useUnits } from "stores/units"
import { usePermissions } from "stores/permissions"
import ShippingOrderCartManualPosition from "models/ShippingOrderCartManualPosition"

function getFreightUnitKey(assetId: Guid, freightUnit: Asset["freightUnits"][number], defaultMeasurementSystemId?: Guid) {
    return [
        assetId,
        freightUnit.description,
        freightUnit.measurementSystemId || defaultMeasurementSystemId || "",
        freightUnit.dimension1 || "",
        freightUnit.dimension2 || "",
        freightUnit.dimension3 || "",
        freightUnit.weight || "",
    ].join("|")
}

function getManualPositionFreightUnitKey(position: ShippingOrderCartManualPosition) {
    if (!position.assetId) return null
    return [
        position.assetId,
        position.description,
        position.measurementSystemId || "",
        position.dimension1 || "",
        position.dimension2 || "",
        position.dimension3 || "",
        position.weight || "",
    ].join("|")
}

interface IUseShippingOrderEditModalParams {
    reservation?: Reservation
    shippingOrder?: ShippingOrder | null
    shippingOrderId?: Guid
    type?: ShippingOrderType
    hideForwardToShippingControls?: boolean
    onClose(): void
    onSave?(params: { forwardToShipping: boolean, freightUnits: ISelectedFreightUnit[] | null }): Promise<void> | void
    onSaved?(): void
    onInitialized?(params: { addAssets: (assetsToAdd: Guid[]) => Promise<ShippingOrderCart> }): void
    onSessionExpired?(): void
}

export interface IShippingOrderHeaderDataChange {
    deliveryTermId?: Guid | null
    responsibleWarehouseId?: Guid | null
    departureDate?: Dayjs | null
    arrivalDate?: Dayjs | null
    personInCharge?: string | null
    personInChargePhone?: string | null
    note?: string
    deliveryNoteHeaderText?: string
    deliveryNoteFooterText?: string
    deliveryNotePrintFeatures?: boolean
}

export enum ShippingOrderEditTab {
    basicData,
    assets,
}

export default function useShippingOrderEditModal({
    reservation,
    shippingOrder,
    shippingOrderId,
    type,
    hideForwardToShippingControls,
    onClose,
    onSave,
    onSaved,
    onInitialized,
    onSessionExpired,
}: IUseShippingOrderEditModalParams) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { hasPermission } = usePermissions()

    const {
        cart,
        isCartBusy,
        updateCart,
        createManualPositionsWithLink,
        deleteManualPosition,
        deleteCart,
        publishCart,
        changeCartStatus,
        addAssets,
        changeAssets,
        deleteAssets,
        reset,
        errorCode,
        errorEmail,
        errorDate,
        setOverrideOwnership,
        isInitCartPending,
        showContinueSessionModal,
        resetSessionTimeout,
        deliveryTerms,
        tenantWarehouses,
        getRequestLocations,
        createRequestLocation,
    } = useShippingOrderCart({
        type,
        reservationId: (shippingOrder?.id || shippingOrderId) ? undefined : reservation?.id,
        shippingOrderId: shippingOrder?.id || shippingOrderId,
        onInitialized,
    })
    const { units } = useUnits()

    const [isSaving, setIsSaving] = useState(false)
    const [isSubmitted, setIsSumbitted] = useState(false)
    const [dataLossWarningEnabled, setDataLossWarningEnabled] = useState(true)
    const [showSelectAssetsModal, setShowSelectAssetsModal] = useState(false)
    const [showUpdateHomebaseModal, setShowUpdateHomebaseModal] = useState(false)
    const [assetsWithFreightUnits, setAssetsWithFreightUnits] = useState<Asset[] | null>(null)
    const [freightUnitsCatalog, setFreightUnitsCatalog] = useState<Asset[] | null>(null)
    const [freightUnitsToCreate, setFreightUnitsToCreate] = useState<ISelectedFreightUnit[] | null>(null)
    const [selectedAssetIds, setSelectedAssetIds] = useState<Guid[]>([])

    const [note, setNote] = useState<string>("")
    const [deliveryTermId, setDeliveryTermId] = useState<string>()
    const [responsibleWarehouseId, setResponsibleWarehouseId] = useState<string>()
    const [dates, setDates] = useState<{ departureDate: Dayjs | null, arrivalDate: Dayjs | null } | null>(null)
    const [personInCharge, setPersonInCharge] = useState("")
    const [personInChargePhone, setPersonInChargePhone] = useState("")
    const [deliveryNoteHeaderText, setDeliveryNoteHeaderText] = useState<string>()
    const [deliveryNoteFooterText, setDeliveryNoteFooterText] = useState<string>()
    const [deliveryNotePrintFeatures, setDeliveryNotePrintFeatures] = useState(false)
    const [forwardToShipping, setForwardToShipping] = useState<boolean>(false)

    const deliveryTerm = deliveryTerms && deliveryTermId ? deliveryTerms.entities.find(dt => dt.termId === deliveryTermId) : undefined
    const responsibleWarehouse = tenantWarehouses && responsibleWarehouseId ? tenantWarehouses.entities.find(w => w.id === responsibleWarehouseId) : undefined

    const shippingOrderAssetIds = useMemo(() => {
        if (!cart || cart.assets.length === 0) return []
        return cart.assets.map((asset) => asset.assetId)
    }, [cart])

    const isForwardToShippingCheckboxShown = !hideForwardToShippingControls && !!hasPermission(UserPermission.ShippingOrdersForwardToShipping)
    const isForwardToShippingCheckboxDisabled = shippingOrderAssetIds.length === 0
    const isSaveAndPrepareLoadingListButtonShown = !onSave && isForwardToShippingCheckboxShown && hasPermission(UserPermission.LoadingListsEdit)
    const isSaveAndPrepareLoadingListButtonDisabled = !forwardToShipping

    const [sort, setSort] = useState<IShippingOrderAssetsSort | null>(null)
    const canAddAssets = !!addAssets
    const canDeleteAssets = !!deleteAssets
    const canChangeAssetLocations = !!changeAssets

    const cartAssetIds = useMemo(() => {
        return cart?.assets?.map(asset => asset.assetId).join(",") || ""
    }, [cart?.assets])

    const getRequestLocationsForCurrentCart = useCallback(async () => {
        return getRequestLocations()
    }, [getRequestLocations, cart?.id, cartAssetIds])

    const { requestLocations, addRequestLocation, refetchRequestLocations } = useRequestLocations({
        getRequestLocations: cart?.linkGetRequestLocations ? getRequestLocationsForCurrentCart : undefined,
    })

    const reservationAssets = useMemo(() => reservation?.assets || [], [reservation])
    const cartAssets = useMemo(() => cart?.assets || [], [cart])

    const { shippingOrderAssetsExtended, isLoadingAssetDisplayStatuses } = useShippingOrderAssetsExtended({
        reservationStartDate: reservation?.time.reservationStart,
        reservationEndDate: reservation?.time.reservationEnd,
        reservationAssets,
        assets: cartAssets,
        sort,
        locations: requestLocations,
        selectAssetsInProgress: showSelectAssetsModal,
    })

    useEffect(() => {
        if (!cart) return
        setNote(cart.note || "")
        setDeliveryTermId(cart.deliveryTermId)
        setResponsibleWarehouseId(cart.responsibleWarehouseId)
        setDates({
            departureDate: cart.departureDate || null,
            arrivalDate: cart.arrivalDate || null,
        })
        setPersonInCharge(cart.personInCharge || "")
        setPersonInChargePhone(cart.personInChargePhone || "")
        setDeliveryNoteHeaderText(cart.deliveryNoteHeaderText)
        setDeliveryNoteFooterText(cart.deliveryNoteFooterText)
        setDeliveryNotePrintFeatures(cart.deliveryNotePrintFeatures)
    }, [cart])

    useEffect(() => {
        if (!cart || cart.assets.length === 0) {
            setForwardToShipping(false)
        }
    }, [cart, cart?.assets.length])

    const isDeliveryTermRequired = !!forwardToShipping
    const isDepartureAndArrivalDateRequired = !!forwardToShipping

    const validationErrors = useMemo((): { [x: string]: CustomError } => {
        const validationFields: [string, IValidationRule, any][] = [
            ["deliveryTermId", { required: isDeliveryTermRequired }, deliveryTermId],
            ["departureDate", { required: isDepartureAndArrivalDateRequired }, dates?.departureDate],
            ["arrivalDate", { required: isDepartureAndArrivalDateRequired }, dates?.arrivalDate],
        ]
        return ValidationService.validateMultiple(validationFields)
    }, [dates?.arrivalDate, deliveryTermId, dates?.departureDate, isDeliveryTermRequired, isDepartureAndArrivalDateRequired])

    const errorTabs = useMemo(() => {
        return {
            [ShippingOrderEditTab.basicData]: Object.keys(validationErrors).length > 0,
            [ShippingOrderEditTab.assets]: cart?.hasAssetsWithoutDepartureOrArrival ?? false,
        }
    }, [cart?.hasAssetsWithoutDepartureOrArrival, validationErrors])

    const { handleDepartureDateChange, handleArrivalDateChange } = useDepartureArrivalDateRules({ dates, setDates })

    const patchCart = useCallback(async (params?: IUpdateShippingOrderCartDto) => {
        return updateCart(params || {
            note,
            deliveryTermId: deliveryTermId || null,
            departureDate: dates?.departureDate ? toDateTimeWithoutTimezone(dates.departureDate) : null,
            arrivalDate: dates?.arrivalDate ? toDateTimeWithoutTimezone(dates?.arrivalDate) : null,
            personInCharge: personInCharge || null,
            personInChargePhone: personInChargePhone || null,
            deliveryNoteHeaderText: deliveryNoteHeaderText || null,
            deliveryNoteFooterText: deliveryNoteFooterText || null,
            deliveryNotePrintFeatures: deliveryNotePrintFeatures ?? undefined,
            responsibleWarehouseId: responsibleWarehouseId || null,
        })
    }, [dates?.arrivalDate, dates?.departureDate, deliveryNoteFooterText, deliveryNoteHeaderText,
        deliveryNotePrintFeatures, deliveryTermId, note, personInCharge, personInChargePhone,
        responsibleWarehouseId, updateCart])

    function handleBasicDataChange(params: Partial<IShippingOrderHeaderDataChange>) {
        if (typeof params.deliveryTermId !== "undefined") {
            setDeliveryTermId(params.deliveryTermId || undefined)
        }
        if (typeof params.responsibleWarehouseId !== "undefined") {
            setResponsibleWarehouseId(params.responsibleWarehouseId || undefined)
        }

        let newDates: { departureDate: Dayjs | null, arrivalDate: Dayjs | null } | null = null
        if (typeof params.departureDate !== "undefined" && !params.departureDate?.isSame(dates?.departureDate)) {
            newDates = handleDepartureDateChange(params.departureDate)
        }
        if (typeof params.arrivalDate !== "undefined" && !params.arrivalDate?.isSame(dates?.arrivalDate)) {
            newDates = handleArrivalDateChange(params.arrivalDate)
        }

        if (typeof params.deliveryNotePrintFeatures !== "undefined") {
            setDeliveryNotePrintFeatures(params.deliveryNotePrintFeatures)
        }

        if (typeof params.personInCharge !== "undefined") {
            setPersonInCharge(params.personInCharge || "")
        }

        if (typeof params.personInChargePhone !== "undefined") {
            setPersonInChargePhone(params.personInChargePhone || "")
        }

        if (typeof params.note !== "undefined") {
            setNote(params.note)
        }

        if (typeof params.deliveryNoteHeaderText !== "undefined") {
            setDeliveryNoteHeaderText(params.deliveryNoteHeaderText)
        }

        if (typeof params.deliveryNoteFooterText !== "undefined") {
            setDeliveryNoteFooterText(params.deliveryNoteFooterText)
        }

        if (typeof params.deliveryNotePrintFeatures !== "undefined") {
            setDeliveryNotePrintFeatures(params.deliveryNotePrintFeatures)
        }

        patchCart({
            deliveryTermId: params.deliveryTermId === null ? null : (params.deliveryTermId || undefined),
            responsibleWarehouseId: params.responsibleWarehouseId === null ? null : (params.responsibleWarehouseId || undefined),
            departureDate: newDates ? (newDates.departureDate ? toDateTimeWithoutTimezone(newDates.departureDate) : null) : undefined,
            arrivalDate: newDates ? (newDates.arrivalDate ? toDateTimeWithoutTimezone(newDates.arrivalDate) : null) : undefined,
            personInCharge: params.personInCharge,
            personInChargePhone: params.personInChargePhone,
            note: params.note,
            deliveryNoteHeaderText: params.deliveryNoteHeaderText,
            deliveryNoteFooterText: params.deliveryNoteFooterText,
            deliveryNotePrintFeatures: params.deliveryNotePrintFeatures,
        })
    }

    const [redirectToPrepareShipping, setRedirectToPrepareShipping] = useState(false)
    function onSaveClick(params: { redirectToPrepareShipping?: boolean } = {}) {
        const _redirectToPrepareShipping = params.redirectToPrepareShipping || false
        setRedirectToPrepareShipping(_redirectToPrepareShipping)
        setIsSumbitted(true)

        if (errorTabs[ShippingOrderEditTab.basicData]
            || errorTabs[ShippingOrderEditTab.assets]) {
            return
        }

        if (forwardToShipping) {
            confirmModals.show({
                title: t("common.attention"),
                maxWidth: "586px",
                children: t("shippingOrders.forwardToShippingConfirm"),
                onConfirm: async close => {
                    close?.()
                    proceedToSelectFreightUnitsModal(_redirectToPrepareShipping)
                }
            })
            return
        }

        proceedToSelectFreightUnitsModal(_redirectToPrepareShipping)
    }

    async function proceedToSelectFreightUnitsModal(_redirectToPrepareShipping: boolean) {
        const shouldOpenFreightUnitsSelection = forwardToShipping
            || hideForwardToShippingControls
            || cart?.status === ShippingOrderStatus.forwardedToShipping

        if (!shouldOpenFreightUnitsSelection) {
            proceedToUpdateHomebaseModal(_redirectToPrepareShipping)
            return
        }

        const getAssetsQueue = new PQueue({ concurrency: 3 })
        const assets: Asset[] = []

        const getAsset = async ({ assetId, articleDescription }: { assetId: Guid, articleDescription: string }) => {
            try {
                const asset = await AssetService.getAsset(assetId)
                if (asset) {
                    asset.articleDescription = articleDescription
                    assets.push(asset)
                }
            } catch (e) {
                console.error("Could not fetch asset", e)
                toastMessages.showSystemError()
            }
        }


        for (const asset of shippingOrderAssetsExtended) {
            getAssetsQueue.add(getAsset.bind(null, {
                assetId: asset.assetId,
                articleDescription: asset.articleDescription,
            }))
        }

        await getAssetsQueue.onIdle()

        const newAssetsWithFreightUnits = assets.filter(asset => asset.freightUnits.length > 0)

        if (newAssetsWithFreightUnits.length) {
            setFreightUnitsCatalog(newAssetsWithFreightUnits)
            setAssetsWithFreightUnits(newAssetsWithFreightUnits)

            if (cart) {
                const selectedFreightUnitsByKey = new Map<string, ISelectedFreightUnit>()

                for (const asset of newAssetsWithFreightUnits) {
                    for (const freightUnit of asset.freightUnits) {
                        if (!asset.id) continue
                        const key = getFreightUnitKey(asset.id, freightUnit, units[0]?.id)
                        selectedFreightUnitsByKey.set(key, {
                            assetId: asset.id,
                            freightUnit,
                        })
                    }
                }

                const currentSelectionByKey = new Map<string, ISelectedFreightUnit>()
                for (const selectedFreightUnit of freightUnitsToCreate || []) {
                    const key = getFreightUnitKey(selectedFreightUnit.assetId, selectedFreightUnit.freightUnit, units[0]?.id)
                    if (selectedFreightUnitsByKey.has(key)) {
                        currentSelectionByKey.set(key, selectedFreightUnit)
                    }
                }

                if (currentSelectionByKey.size === 0) {
                    for (const manualPosition of cart.manualPositions) {
                        const key = getManualPositionFreightUnitKey(manualPosition)
                        if (!key) continue
                        const selectedFreightUnit = selectedFreightUnitsByKey.get(key)
                        if (selectedFreightUnit) {
                            currentSelectionByKey.set(key, selectedFreightUnit)
                        }
                    }
                }

                const selectedFreightUnits = Array.from(currentSelectionByKey.values())
                setFreightUnitsToCreate(selectedFreightUnits.length ? selectedFreightUnits : null)
            }

            return
        }

        proceedToUpdateHomebaseModal(_redirectToPrepareShipping)
    }

    async function handleFreightUnitsSelection(freightUnits: ISelectedFreightUnit[]) {
        setFreightUnitsToCreate(freightUnits)
        setAssetsWithFreightUnits(null)
        proceedToUpdateHomebaseModal(redirectToPrepareShipping, freightUnits)
    }

    const assetsToUpdateHomebase = useMemo(() => {
        if (!cart || cart.type !== ShippingOrderType.return) return []
        return shippingOrderAssetsExtended.filter(a => {
            return a.arrivalLocation
                && a.arrivalLocation.type === RequestLocationType.warehouse
                && !!a.arrivalLocation.warehouseId
                && a.homebaseId !== a.arrivalLocation.warehouseId
        })
    }, [shippingOrderAssetsExtended, cart])

    async function proceedToUpdateHomebaseModal(_redirectToPrepareShipping: boolean = redirectToPrepareShipping, freightUnits: ISelectedFreightUnit[] | null = freightUnitsToCreate) {
        if (assetsToUpdateHomebase.length) {
            setShowUpdateHomebaseModal(true)
            return
        }
        await handleSubmitConfirm(_redirectToPrepareShipping, freightUnits)
    }

    async function updateAssetsHomebase(assetsToUpdate: ShippingOrderAssetExtended[]) {
        const assetsWithWarehouseHomebase = assetsToUpdate.filter(asset => !!asset.arrivalLocation?.warehouseId)

        if (assetsWithWarehouseHomebase.length) {
            await AssetService.updateAssets(assetsWithWarehouseHomebase.map(a => ({
                assetId: a.assetId,
                homebaseId: a.arrivalLocation?.warehouseId,
            })))
        }
        handleSubmitConfirm()
    }

    async function createFreightUnitsIfNeeded(currentCart: ShippingOrderCart, freightUnits: ISelectedFreightUnit[] | null = freightUnitsToCreate) {
        const catalogByKey = new Map<string, ISelectedFreightUnit>()
        for (const asset of freightUnitsCatalog || []) {
            if (!asset.id) continue
            for (const freightUnit of asset.freightUnits) {
                catalogByKey.set(getFreightUnitKey(asset.id, freightUnit, units[0]?.id), {
                    assetId: asset.id,
                    freightUnit,
                })
            }
        }

        if (catalogByKey.size === 0) {
            return
        }

        const selectedByKey = new Map<string, ISelectedFreightUnit>()
        for (const selectedFreightUnit of freightUnits || []) {
            const key = getFreightUnitKey(selectedFreightUnit.assetId, selectedFreightUnit.freightUnit, units[0]?.id)
            if (catalogByKey.has(key)) {
                selectedByKey.set(key, selectedFreightUnit)
            }
        }

        const getFreightUnitPositionIdsByKey = (cartToInspect: ShippingOrderCart) => {
            const result = new Map<string, Guid[]>()
            for (const position of cartToInspect.manualPositions) {
                const key = getManualPositionFreightUnitKey(position)
                if (!key || !catalogByKey.has(key)) continue
                const ids = result.get(key) || []
                ids.push(position.id)
                result.set(key, ids)
            }
            return result
        }

        let workingCart = currentCart
        let freightUnitPositionIdsByKey = getFreightUnitPositionIdsByKey(workingCart)

        if (deleteManualPosition) {
            for (const [key, positionIds] of freightUnitPositionIdsByKey) {
                if (selectedByKey.has(key)) continue
                for (const positionId of positionIds) {
                    workingCart = await deleteManualPosition(positionId)
                }
            }
            freightUnitPositionIdsByKey = getFreightUnitPositionIdsByKey(workingCart)
        }

        const positionsToCreate = Array.from(selectedByKey.entries())
            .filter(([key]) => !(freightUnitPositionIdsByKey.get(key)?.length))
            .map(([_, freightUnit]) => {
                const soAsset = workingCart.assets.find(a => a.assetId === freightUnit.assetId)
                return {
                    assetId: freightUnit.assetId,
                    description: freightUnit.freightUnit.description,
                    measurementSystemId: freightUnit.freightUnit.measurementSystemId ?? units[0].id,
                    dimension1: freightUnit.freightUnit.dimension1,
                    dimension2: freightUnit.freightUnit.dimension2,
                    dimension3: freightUnit.freightUnit.dimension3,
                    weight: freightUnit.freightUnit.weight,
                    departureLocationId: soAsset?.departureLocationId ?? "",
                    arrivalLocationId: soAsset?.arrivalLocationId ?? "",
                }
            })

        if (positionsToCreate.length && workingCart.linkCreateManualPositions) {
            workingCart = await createManualPositionsWithLink({
                link: workingCart.linkCreateManualPositions,
                data: positionsToCreate,
            })
            freightUnitPositionIdsByKey = getFreightUnitPositionIdsByKey(workingCart)
        }

        const selectedFreightUnits = Array.from(selectedByKey.values())
        setFreightUnitsToCreate(selectedFreightUnits.length ? selectedFreightUnits : null)
    }

    async function handleSubmitConfirm(_redirectToPrepareShipping: boolean = redirectToPrepareShipping, freightUnits: ISelectedFreightUnit[] | null = freightUnitsToCreate) {
        setIsSaving(true)
        try {
            const patchedCart = await patchCart()

            if (onSave) {
                if (!cart) {
                    console.error("Cart not found")
                    toastMessages.showSystemError()
                    return
                }
                await onSave({ forwardToShipping, freightUnits })
                setIsSaving(false)
            } else {
                let currentCartForFreightUnits = patchedCart

                if (forwardToShipping) {
                    if (!changeCartStatus) {
                        console.error("Not allowed to change cart status (no changeCartStatus function)")
                        throw new CustomError({
                            code: ErrorCode.systemError,
                        })
                    }
                    const newCart = await changeCartStatus({ status: ShippingOrderStatus.forwardedToShipping })
                    currentCartForFreightUnits = newCart
                }

                if (currentCartForFreightUnits) {
                    await createFreightUnitsIfNeeded(currentCartForFreightUnits, freightUnits)
                }

                const publishedShippingOrderId = await publishCart({ triggerShippingOrderChangedNotification: true })
                toastMessages.showDataSaved()
                if (_redirectToPrepareShipping) {
                    reset()
                    leaveWithoutConfirm(Routes.prepareShipping.getPath({ shippingOrderId: publishedShippingOrderId }))
                } else {
                    setIsSaving(false)
                    onSaved?.()
                    leaveWithoutConfirm()
                }
            }
        } catch (e) {
            toastMessages.showSystemError()
            setIsSaving(false)
            return
        }
    }

    async function handleClose() {
        await deleteCart()
        reset()
        onClose()
    }

    function handleSessionExpired() {
        leaveWithoutConfirm()
        onSessionExpired?.()
    }

    function leaveWithoutConfirm(path?: string) {
        flushSync(() => {
            setDataLossWarningEnabled(false)
        })
        if (path) {
            navigate(path)
        } else {
            onClose()
        }
    }

    async function handleAddAssets(assetIds: Guid[]) {
        if (!assetIds.length) return
        if (!addAssets) {
            throw new CustomError({ code: ErrorCode.systemError })
        }
        try {
            await addAssets(assetIds)
        } catch (e) {
            toastMessages.showSystemError()
            throw e
        }
    }

    async function handleDeleteAsset(assetId: Guid) {
        await handleDeleteAssets([assetId])
        onAssetSelectionChange(selectedAssetIds.filter(id => id !== assetId))
    }

    async function handleDeleteAssets(assetIds: Guid[]) {
        if (!assetIds.length) return
        if (!deleteAssets) {
            throw new CustomError({ code: ErrorCode.systemError })
        }
        try {
            const updatedCart = await deleteAssets(assetIds)

            if (deleteManualPosition) {
                for (const position of updatedCart.manualPositions) {
                    if (!position.assetId || !assetIds.includes(position.assetId)) continue
                    await deleteManualPosition(position.id)
                }
            }

            setFreightUnitsToCreate(current => {
                if (!current) return null
                const updated = current.filter(freightUnit => !assetIds.includes(freightUnit.assetId))
                return updated.length ? updated : null
            })
            setFreightUnitsCatalog(current => {
                if (!current) return null
                const updated = current.filter(asset => !asset.id || !assetIds.includes(asset.id))
                return updated.length ? updated : null
            })
            setAssetsWithFreightUnits(current => {
                if (!current) return null
                const updated = current.filter(asset => !asset.id || !assetIds.includes(asset.id))
                return updated.length ? updated : null
            })
        } catch (e) {
            toastMessages.showSystemError()
            throw e
        }
    }

    async function onAssetsSelect(assetsToAdd: Guid[], assetsToDelete: Guid[]) {
        await handleAddAssets(assetsToAdd)
        await handleDeleteAssets(assetsToDelete)
    }

    const changeAssetDepartureArrivalLocation = useCallback(async (assetIds: Guid[], changes: Pick<IUpdateShippingOrderCartAssetDto, "departureLocationId" | "arrivalLocationId">) => {
        await patchCart()
        await changeAssets?.({
            assetIds,
            ...changes,
        })
    }, [changeAssets, patchCart])

    const handleCreateRequestLocation = useMemo(() => {
        if (!cart?.linkCreateRequestLocation) return
        return async (assetIds: Guid[], isUnloading: boolean, data: ICreateRequestLocation) => {
            const requestLocationId = await createRequestLocation(data)
            addRequestLocation(requestLocationId, data)
            await changeAssetDepartureArrivalLocation(
                assetIds,
                isUnloading
                    ? { arrivalLocationId: requestLocationId }
                    : { departureLocationId: requestLocationId },
            )
            await refetchRequestLocations()
        }
    }, [addRequestLocation, cart?.linkCreateRequestLocation, changeAssetDepartureArrivalLocation, createRequestLocation, refetchRequestLocations])

    const onAssetSelectionChange = useCallback((newSelectedAssetIds: Guid[]) => {
        const uniqueNewSelectedAssetIds = uniq(newSelectedAssetIds)
        setSelectedAssetIds(uniqueNewSelectedAssetIds)
    }, [])

    return {
        cart,
        isCartBusy,
        note,
        deliveryTerm,
        responsibleWarehouse,
        dates,
        personInCharge,
        personInChargePhone,
        forwardToShipping,
        setForwardToShipping,
        isInitCartPending,
        isDeliveryTermRequired,
        isDepartureAndArrivalDateRequired,
        onSaveClick,
        isSaving,
        isSubmitted,
        validationErrors,
        errorTabs,
        handleClose,
        dataLossWarningEnabled,
        showContinueSessionModal,
        resetSessionTimeout,
        leaveWithoutConfirm,
        handleSessionExpired,
        errorCode,
        errorEmail,
        errorDate,
        setOverrideOwnership,
        deliveryTerms,
        tenantWarehouses,
        showSelectAssetsModal,
        setShowSelectAssetsModal,
        shippingOrderAssetIds,
        onAssetsSelect,
        handleDeleteAsset,
        handleDeleteAssets,
        isForwardToShippingCheckboxShown,
        isForwardToShippingCheckboxDisabled,
        isSaveAndPrepareLoadingListButtonShown,
        isSaveAndPrepareLoadingListButtonDisabled,
        handleBasicDataChange,
        createRequestLocation: handleCreateRequestLocation,
        changeAssetDepartureArrivalLocation,
        showUpdateHomebaseModal,
        setShowUpdateHomebaseModal,
        assetsWithFreightUnits,
        setAssetsWithFreightUnits,
        freightUnitsToCreate,
        setFreightUnitsToCreate,
        handleFreightUnitsSelection,
        updateAssetsHomebase,
        requestLocations,
        refetchRequestLocations,
        sort,
        setSort,
        shippingOrderAssetsExtended,
        isLoadingAssetDisplayStatuses,
        assetsToUpdateHomebase,
        selectedAssetIds,
        onAssetSelectionChange,
        canAddAssets,
        canDeleteAssets,
        canChangeAssetLocations,
        deliveryNoteHeaderText,
        deliveryNoteFooterText,
        deliveryNotePrintFeatures,
    }
}
